import React from "react";
import {Link, useLocation} from "react-router-dom";
import Button from "@source/components/UI/buttons/button/button";
import CacheImg from "@source/components/UI/cacheImg/cacheImg";
import PhotoCellsRow from "@source/components/photoCellsRow/photoCellsRow";
import Svg from "@source/components/UI/svg/svg";
import {stringToHslColor} from "@source/utils/utils";
import iconAttention from "@source/assets/icons/iconAttention.svg";
import {
  ConsentCategorySource,
  ConsentDocument,
  ConsentSignerRole,
  ConsentSignatoryState,
} from "@source/api/models/consentModels";
import {
  canPresentClinicalNow,
  canPresentFamilyNow,
  canPresentResidentNow,
  clinicalPresentationPath,
  familyPresentationPath,
  consentDocumentDetailPath,
  employeeSignPath,
  formatConsentSender,
  getConsentRoleProgress,
  residentPresentationPath,
  shouldDisplayConsentCategoryName,
} from "@source/api/models/consentHelpers";
import userStore from "@source/store/userStore";
import {canManageConsentDocuments, canViewConsentDocuments} from "@source/utils/consentAccess";
import {formatConsentDate} from "./consentDate";
import {canCurrentEmployeeSign, employeeSignAccessFromUser} from "./employeeSigningHelpers";
import {ConsentStatusLabel} from "./consentStatusLabel";
import styles from "./consentShared.module.scss";

const signedSignerPhotos = (document: ConsentDocument) => {
  const people = [
    document.resident,
    ...(document.requiredEmployeeSigners || []),
    ...(document.familyMembers || []),
    ...(document.familyMember ? [document.familyMember] : []),
  ].filter(Boolean);

  return (document.signatories || [])
    .filter((signatory) => signatory.signed || signatory.state === ConsentSignatoryState.SIGNED)
    .map((signatory) => {
      const person = people.find((candidate) => candidate._id === signatory.id);
      const name = person
        ? `${person.firstName} ${person.lastName}`
        : signatory.name;
      return {
        id: signatory.id ? `${signatory.role}-${signatory.id}` : signatory.role,
        photo: person?.photo,
        abbreviatedName: name.split(" ").filter(Boolean).map((part) => part.charAt(0).toUpperCase()).join("").slice(0, 2) || "S",
      };
    });
};

const CompactSignerProgress = ({document, omitEmployeeTotal = false}: {document: ConsentDocument; omitEmployeeTotal?: boolean}) => {
  const groups = getConsentRoleProgress(document.signatories);
  if (!groups.length) return <span className={styles.unavailable}>Signing details unavailable</span>;

  return (
    <div className={styles.compactSignerProgress}>
      <PhotoCellsRow photosData={signedSignerPhotos(document)}/>
      <div className={styles.compactProgressText}>
        {groups.map((group) => (
          <span key={group.role}>{group.label.replace(/s$/, "")} signatures: {group.signed}{omitEmployeeTotal && group.role === ConsentSignerRole.EMPLOYEE ? "" : ` of ${group.total}`}</span>
        ))}
      </div>
    </div>
  );
};

export interface ConsentDocumentCardProps {
  document: ConsentDocument;
  onClick?: (document: ConsentDocument) => void;
  actions?: React.ReactNode;
  link?: string;
  /** The employee queue endpoint already authorizes every returned document. */
  employeeSigningCard?: boolean;
  variant?: "default" | "editableDraft";
  className?: string;
  omitEmployeeProgressTotal?: boolean;
}

export const ConsentDocumentCard = ({
  document,
  onClick,
  actions,
  link,
  employeeSigningCard = false,
  variant = "default",
  className = "",
  omitEmployeeProgressTotal = false,
}: ConsentDocumentCardProps) => {
  const canCurrentEmployeeSignDocument = canCurrentEmployeeSign(
    document,
    userStore.getId(),
    employeeSignAccessFromUser(userStore.getUserInfo(), employeeSigningCard),
  );
  const showSignAction = variant !== "editableDraft"
    && canCurrentEmployeeSignDocument;
  const canPresent = variant !== "editableDraft"
    && canViewConsentDocuments() && canManageConsentDocuments() && canPresentResidentNow(document);
  const canPresentClinical = variant !== "editableDraft"
    && canViewConsentDocuments() && canManageConsentDocuments() && canPresentClinicalNow(document);
  const canPresentFamily = variant !== "editableDraft"
    && canViewConsentDocuments() && canManageConsentDocuments() && canPresentFamilyNow(document);
  const location = useLocation();
  const destination = link || consentDocumentDetailPath(document._id);
  const destinationState = {returnTo: `${location.pathname}${location.search}`};
  const cardActions = showSignAction || canPresent || canPresentClinical || canPresentFamily || actions
    ? <>{showSignAction && <Button size="small" event="link" link={employeeSignPath(document._id)} className={styles.signBtn}>Sign</Button>}{canPresent && <Button size="small" event="link" link={residentPresentationPath(document.resident._id, document._id)} className={styles.signBtn}>Present To Resident</Button>}{canPresentClinical && <Button size="small" event="link" link={clinicalPresentationPath(document.resident._id, document._id)} className={styles.signBtn}>Present To GP</Button>}{canPresentFamily && <Button size="small" event="link" link={familyPresentationPath(document.resident._id, document._id)} className={styles.signBtn}>Present To Family</Button>}{actions}</>
    : null;
  const residentName = [document.resident?.firstName, document.resident?.lastName]
    .filter(Boolean)
    .join(" ") || "Resident unavailable";
  const residentFirstName = document.resident?.firstName || "";
  const residentLastName = document.resident?.lastName || "";
  const initials = `${residentFirstName[0]?.toUpperCase() || ""} ${residentLastName[0]?.toUpperCase() || ""}`.trim() || "R";
  const categoryName = document.categorySnapshot?.name;
  const displayCategoryName = shouldDisplayConsentCategoryName(document.title, categoryName)
    ? categoryName
    : "";
  const sourceLabel = document.categorySnapshot?.source === ConsentCategorySource.TEMPLATE
    ? "Template"
    : document.categorySnapshot?.source === ConsentCategorySource.RAW_PDF
      ? "Existing PDF"
      : document.source === ConsentCategorySource.TEMPLATE
        ? "Template"
        : document.source === ConsentCategorySource.RAW_PDF
          ? "Existing PDF"
          : "";
  const creatorName = [document.createdBy?.firstName, document.createdBy?.lastName].filter(Boolean).join(" ");
  const createdDate = formatConsentDate(document.createdAt, "");
  const sender = formatConsentSender(document.createdBy);
  const draftByline = creatorName && createdDate
    ? `(Draft by ${creatorName}, ${createdDate})`
    : creatorName
      ? `(Draft by ${creatorName})`
      : createdDate
        ? `(Draft, ${createdDate})`
        : "(Draft)";

  if (variant === "editableDraft") {
    return (
      <article className={`${styles.card} ${styles.draftCard} ${className}`}>
        <div className={styles.draftRail} aria-hidden="true">
          <Svg className={styles.draftAttentionIcon} src={iconAttention} width={16} height={16} color="#ffffff"/>
        </div>
        <div className={styles.draftContent}>
          <Link className={styles.photoLink} to={`/home/residents/${document.resident._id}`} aria-label={`View ${residentName}`}>
            <CacheImg url={document.resident?.photo} alt={`${residentName} avatar`} className={styles.photo} imgClassName={styles.photo} skeletonProps={{variant: "circular"}}>
              <div className={styles.noPhoto} style={{backgroundColor: stringToHslColor(residentFirstName + residentLastName)}}>{initials}</div>
            </CacheImg>
          </Link>
          <div className={styles.cardIdentity}>
            <div className={styles.draftTitleRow}><h3>{residentName}</h3><span className={styles.draftByline}>{draftByline}</span></div>
            <div className={styles.draftMeta}>
              <span>{document.title || "Untitled consent document"}</span>
              {(displayCategoryName || sourceLabel) && <><span className={styles.metaDivider} aria-hidden="true">|</span><span>{[displayCategoryName, sourceLabel].filter(Boolean).join(" · ")}</span></>}
            </div>
            {sender ? <span className={styles.sender}>{sender}</span> : null}
          </div>
          {link && <Button className={styles.continueButton} event="link" link={link}>Continue</Button>}
        </div>
      </article>
    );
  }

  const residentPhoto = (
    <Link className={styles.photoLink} to={`/home/residents/${document.resident._id}`} aria-label={`View ${residentName}`}>
      <CacheImg url={document.resident?.photo} alt={`${residentName} avatar`} className={styles.photo} imgClassName={styles.photo} skeletonProps={{variant: "circular"}}>
        <div className={styles.noPhoto} style={{backgroundColor: stringToHslColor(residentFirstName + residentLastName)}}>{initials}</div>
      </CacheImg>
    </Link>
  );
  const content = (
    <div className={styles.cardContent}>
      {residentPhoto}
      <div className={styles.cardIdentity}>
        <div className={styles.cardTitleRow}><h3>{residentName}</h3><span className={styles.documentTitle}>{document.title || "Untitled consent document"}</span></div>
        <div className={styles.meta}>{displayCategoryName || !document.title ? <span>{categoryName || "Legacy consent document"}</span> : null}</div>
        {sender ? <span className={styles.sender}>{sender}</span> : null}
        <CompactSignerProgress document={document} omitEmployeeTotal={omitEmployeeProgressTotal}/>
      </div>
      <div className={styles.cardAside}>
        <div className={styles.cardStatus}>
          <ConsentStatusLabel status={document.status} displayStatus={document.displayStatus} activeStages={document.activeStages} clinicalSigningMode={document.clinicalSigningMode} familySigningMode={document.familySigningMode} signatories={document.signatories}/>
          <time dateTime={document.sentAt || document.createdAt}>{document.sentAt ? `Sent: ${formatConsentDate(document.sentAt)}` : `Created: ${formatConsentDate(document.createdAt)}`}</time>
        </div>
        {cardActions && <div className={styles.cardActions}>{cardActions}</div>}
      </div>
    </div>
  );

  if (destination || onClick) {
    const cardLabel = `${residentName}: ${document.title || "Untitled consent document"}`;
    return (
      <article className={`${styles.card} ${styles.cardInteractive} ${cardActions ? styles.cardWithActions : ""} ${className}`}>
        {destination
          ? <Link className={styles.cardLink} to={{pathname: destination, state: destinationState}} aria-label={cardLabel}/>
          : <button type="button" className={styles.cardButton} onClick={() => onClick?.(document)} aria-label={cardLabel}/>}
        {content}
      </article>
    );
  }
  return (
    <article className={`${styles.card} ${cardActions ? styles.cardWithActions : ""} ${className}`}>
      {content}
    </article>
  );
};
